'use client'
import React, { useContext } from 'react'
import Section from 'components/Section'
import { useMemoizedContentGetter } from 'utils/contentful'
import { useViewportSmallerThan } from 'utils/viewport'
import { BREAKPOINTS } from 'constants/viewport'
import { TitleDivider } from 'components/TitleDivider'
import { CarouselV2 } from 'components/Carousels/CarouselV2'
import { MediaImageStandard, TypographyLongForm, LinkButton } from 'components/Contentful'
import { VideoDialog } from 'components/VideoDialog'
import { StatGrid } from 'components/StatGrid'
import classNames from 'classnames'
import { BLOCKS } from '@contentful/rich-text-types'
import { SITE_ENVIRONMENTS } from 'constants/websites'
import { LayoutContext } from 'providers/LayoutProvider'

const ItemComponentCarousel = ({ item, index, activeIndex, showTitle }) => {
  const isMobile = useViewportSmallerThan(BREAKPOINTS.lg)
  return (
    <div className={'relative'}>
      <div className={'[&_img]:rounded-[10px] overflow-hidden'}>
        <MediaImageStandard
          content={item?.image}
          layout="fill"
          height={isMobile ? 208 : 364}
          width={isMobile ? 208 : 394}
          objectFit={'cover'}
        />
      </div>
      {activeIndex === index && showTitle && (
        <div
          className={
            'bg-[linear-gradient(90deg,#dff7fd_0%,#e4dafb_68.43%,#ddf3ff_95.43%)] shadow-[0_1.6553px_3.3106px_0_rgb(16_24_40/5%)] text-[#154199] absolute -translate-x-2/4 whitespace-nowrap px-4 py-2 rounded-[10px] left-2/4 -bottom-5 animate-fade-up'
          }
        >
          <TypographyLongForm
            variant="bodySm"
            fontWeight="bold"
            component="span"
            content={item?.title}
          />
        </div>
      )}
    </div>
  )
}

const ItemComponentMessage = ({ item, shouldAutoplay = false }) => {
  const siteEnv = process.env.NEXT_PUBLIC_SITE_ENVIRONMENT || 'achieve'

  return (
    <>
      <div
        className={'[&_strong]:font-medium! max-w-[803px] pb-2'}
        data-testid="testimonial-copy-container"
      >
        <TypographyLongForm content={item?.message} className="bodySm lg:bodyLg" />
      </div>
      {item?.statGrid && (
        <div className={'w-full pb-8'}>
          <StatGrid grid={item?.statGrid?.fields} />
        </div>
      )}
      {item?.videoDialog && (
        <VideoDialog
          content={item?.videoDialog}
          trackOpen={{
            nav_link_section: 'Testimonials',
          }}
          autoOpen={shouldAutoplay}
        />
      )}
      {item?.disclaimer && (
        <TypographyLongForm
          content={item?.disclaimer}
          className={classNames(
            'text-[#42546b]  text-center max-w-[803px] w-full',
            siteEnv === SITE_ENVIRONMENTS.FDR ? 'bodyXs! pt-0' : 'pt-8'
          )}
        />
      )}
    </>
  )
}

function TestimonialCarouselV2({ content }) {
  const {
    title,
    eyebrow,
    gridDebtConsolidationTestimonials,
    event,
    backgroundConfigJson,
    config,
    linkButton,
  } = useMemoizedContentGetter(content, [
    'eyebrow',
    'title',
    'gridDebtConsolidationTestimonials',
    'event',
    'backgroundConfigJson',
    'config',
    'linkButton',
  ])
  return (
    <TestimonialCarouselV2Module
      title={title}
      eyebrow={eyebrow}
      gridDebtConsolidationTestimonials={gridDebtConsolidationTestimonials?.gridItemsCollection}
      event={event}
      jsonConfig={backgroundConfigJson?.jsonContent}
      config={config?.jsonContent}
      linkButton={linkButton}
    />
  )
}

function TestimonialCarouselV2Module({
  title,
  eyebrow,
  gridDebtConsolidationTestimonials,
  event,
  jsonConfig,
  config,
  linkButton,
}) {
  const { items = [] } = gridDebtConsolidationTestimonials || {}
  const backgroundConfig = jsonConfig?.background ?? 'bgDefault'
  const variationDot = config?.variationDot ?? null
  const { state: { siteEnv } = {} } = useContext(LayoutContext)

  return (
    <Section
      className={'flex flex-col px-0 py-12 lg:py-16 lg:px-0'}
      contain={true}
      variant={backgroundConfig}
      data-testid="TestimonialCarouselV2_section"
    >
      {(title || eyebrow) && (
        <TitleDivider
          disableTitleTag={true}
          eyebrow={eyebrow}
          eyebrowBlack
          title={title}
          eyebrowClassName={
            title
              ? 'tracking-[3.5px] leading-[16.8px] pb-2 md:pb-4'
              : 'tracking-[3.5px] leading-[16.8px] pb-8'
          }
          className={eyebrow && !title ? 'mb-0' : ''}
        />
      )}

      <CarouselV2
        event={event}
        gridItems={items}
        ItemComponentCarousel={ItemComponentCarousel}
        ItemComponentMessage={ItemComponentMessage}
        withMessages
        config={config}
        variationDot={variationDot}
      />
      {linkButton && (
        <div
          className={classNames(
            'flex flex-col justify-center items-center w-full',
            `${'mt-0 sm:mt-[16px] lg:mt-[48px]'} ${
              siteEnv === SITE_ENVIRONMENTS.FDR ? 'mt-8' : 'mt-12'
            }`
          )}
        >
          <LinkButton
            className={classNames('min-w-[293px] h-[47px]', {
              'bg-[#cb000e]': siteEnv === SITE_ENVIRONMENTS.FDR,
            })}
            content={linkButton}
            typographicOptions={{
              variantOverride: { [BLOCKS.PARAGRAPH]: 'bodyLg' },
              fontWeight: 'bold',
              'data-testid': `testimonials-button-text`,
              className: 'min-w-[105px] text-center',
            }}
            color={config.buttonColor ? config.buttonColor : 'primary'}
            data-testid={`value-prop-button-testimonials`}
            track={{
              ...{
                list_name: 'Personalized Testimonial CTA',
                click_id: linkButton.linkText?.json.content?.[0]?.content[0]?.value,
                click_text: linkButton.linkText?.json.content[0]?.content[0]?.value,
                click_url: linkButton.linkHref,
                nav_link_section: config.navLinkSection ?? 'Value Props',
                click_position: '0',
                track_event: 'internal_campaign_click',
                event_action: 'button_click',
                event_type: 'button_click',
              },
              ...linkButton.event,
            }}
          />
        </div>
      )}
    </Section>
  )
}

export { ItemComponentCarousel, ItemComponentMessage, TestimonialCarouselV2 }

export default TestimonialCarouselV2
